/**
 * Cashflow Dashboard (v2) — display helpers: verdict/state colour semantics +
 * Indian-format money. Colour is used with meaning (brief Part 2):
 * green=verified, amber=caution, red=problem, blue=likely, violet=AI.
 */
import { AgingKey, LeakClass, ReconClass } from './types';

/** Verdict pill — label + classes. The single strongest scanning signal (brief). */
export const VERDICT: Record<LeakClass, { label: string; pill: string; bar: string; text: string }> = {
	REAL: {
		label: 'REAL',
		pill: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
		bar: 'bg-emerald-500',
		text: 'text-emerald-600',
	},
	LIKELY: {
		label: 'LIKELY',
		pill: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
		bar: 'bg-blue-500',
		text: 'text-blue-600',
	},
	PARTIAL: {
		label: 'PARTIAL',
		pill: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
		bar: 'bg-amber-500',
		text: 'text-amber-600',
	},
	NOT_REAL: {
		label: 'NOT REAL',
		pill: 'bg-red-100 text-red-700 ring-1 ring-red-200',
		bar: 'bg-red-500',
		text: 'text-red-600',
	},
};

export const verdictOf = (c?: string) => VERDICT[(c as LeakClass)] || VERDICT.NOT_REAL;

/** State pill (the journey). */
export const STATE_PILL: Record<string, string> = {
	reconciled: 'bg-emerald-50 text-emerald-700',
	disputed: 'bg-red-50 text-red-700',
	claimed_paid: 'bg-amber-50 text-amber-700',
	awaiting: 'bg-slate-100 text-slate-500',
};

/** relevance.category → chip. */
export const CATEGORY: Record<string, { label: string; cls: string }> = {
	receivable: { label: '💰 Receivable', cls: 'bg-emerald-50 text-emerald-700' },
	payable: { label: '💸 Payable', cls: 'bg-amber-50 text-amber-700' },
	both: { label: '⇄ Both', cls: 'bg-violet-50 text-violet-700' },
	not_cashflow: { label: '◷ Not cash flow', cls: 'bg-slate-100 text-slate-500' },
};

/** todo owner → tag. */
export const TODO_OWNER: Record<string, { label: string; cls: string }> = {
	infinitybox_ops: { label: 'OPS', cls: 'bg-blue-100 text-blue-700' },
	accounts: { label: 'ACCOUNTS', cls: 'bg-violet-100 text-violet-700' },
	client: { label: 'CLIENT', cls: 'bg-amber-100 text-amber-700' },
};

/** Compact Indian rupee: ₹1.23 Cr / ₹4.5 L / ₹6,789. */
export const inr = (v: string | number | null | undefined): string => {
	const n = Math.round(Number(v) || 0);
	if (n >= 1e7) return '₹' + (n / 1e7).toFixed(2) + ' Cr';
	if (n >= 1e5) return '₹' + (n / 1e5).toFixed(1) + ' L';
	return '₹' + n.toLocaleString('en-IN');
};

/** Full rupee with grouping: ₹1,45,478. */
export const inrFull = (v: string | number | null | undefined): string =>
	v == null || v === '' ? '—' : '₹' + Math.round(Number(v) || 0).toLocaleString('en-IN');

/** Shorten a long legal customer name for table display. */
export const shortCustomer = (name?: string): string =>
	(name || '—').replace(/\s+(PRIVATE|LIMITED|LTD|PVT|INDIA|SERVICES)\b.*/i, '').trim() || name || '—';

/**
 * recon_class pill — the 5-class Zoho-gated verdict (spec §3b/§4b).
 * Emerald & Slate tokens: ruby #b02d29 (disputes), error #ba1a1a (leak),
 * emerald strictly for reconciled/success; chips = 10% fill + full-chroma text.
 */
export const RECON: Record<ReconClass, { label: string; verb: string; pill: string; bar: string; text: string }> = {
	DISPUTED: {
		label: 'DISPUTED',
		verb: 'resolve',
		pill: 'bg-[#b02d29]/10 text-[#b02d29]',
		bar: 'bg-[#b02d29]',
		text: 'text-[#b02d29]',
	},
	LEAK: {
		label: 'LEAK',
		verb: 'reconcile receipt',
		pill: 'bg-[#ba1a1a]/10 text-[#ba1a1a]',
		bar: 'bg-[#ba1a1a]',
		text: 'text-[#ba1a1a]',
	},
	OPEN: {
		label: 'OPEN',
		verb: 'chase',
		pill: 'bg-amber-600/10 text-amber-700',
		bar: 'bg-amber-500',
		text: 'text-amber-600',
	},
	PAID_UNPROVEN: {
		label: 'PAID UNPROVEN',
		verb: 'verify collection',
		pill: 'bg-slate-500/10 text-slate-600',
		bar: 'bg-slate-400',
		text: 'text-slate-600',
	},
	RECONCILED: {
		label: 'RECONCILED',
		verb: 'done',
		pill: 'bg-emerald-600/10 text-emerald-700',
		bar: 'bg-emerald-600',
		text: 'text-emerald-700',
	},
};

/** Unknown/missing class degrades to OPEN (amber "chase") — never crashes. */
export const reconOf = (c?: string | null) => RECON[c as ReconClass] || RECON.OPEN;

/** Aging buckets, best→worst (ribbon + lens order derives from this). */
export const AGING_ORDER: AgingKey[] = ['not_due', '0-30', '30-60', '60-90', '90+'];

export const AGING_META: Record<AgingKey, { label: string; seg: string; text: string }> = {
	not_due: { label: 'not yet due', seg: 'bg-slate-300', text: 'text-slate-500' },
	'0-30': { label: '0–30d', seg: 'bg-emerald-400', text: 'text-emerald-600' },
	'30-60': { label: '30–60d', seg: 'bg-amber-400', text: 'text-amber-600' },
	'60-90': { label: '60–90d', seg: 'bg-orange-400', text: 'text-orange-600' },
	'90+': { label: '90+ d', seg: 'bg-red-500', text: 'text-red-600' },
};

/** ISO/date string → "30 Mar 25" (local time); null-safe. */
export const fmtDate = (s?: string | null): string =>
	s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

/** master follow_up.action → compact action chip (◆ Verify / ▲ Send / ● Hold / ▲ Open). */
export const fuActionOf = (a?: string | null): { label: string; cls: string } | null => {
	if (!a) return null;
	const s = a.toLowerCase();
	if (s.includes('verify')) return { label: '◆ Verify', cls: 'text-blue-600' };
	if (s.includes('send')) return { label: '▲ Send', cls: 'text-amber-600' };
	if (s.includes('hold')) return { label: '● Hold', cls: 'text-emerald-600' };
	if (s.includes('open')) return { label: '▲ Open', cls: 'text-amber-600' };
	return { label: a, cls: 'text-slate-500' };
};
